import './app.css';
import { Badge, Button, Layout, Menu, Select, Tooltip } from 'antd';
import {
    BulbOutlined,
    MoonOutlined,
    PoweroffOutlined,
    SettingOutlined,
    ContactsOutlined,
    UnorderedListOutlined,
    ShoppingCartOutlined,
    BankOutlined,
    TeamOutlined,
    AppstoreOutlined,
    MessageOutlined,
    ToolOutlined,
    DashboardOutlined,
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { createBrowserRouter, RouterProvider, useNavigate, useLocation, Navigate } from 'react-router-dom';
import { memo, useEffect, useState } from 'react';
import Auth from './components/auth/auth';
import Tasks from './components/tasks/tasks';
import Dashboard from './components/tasks/dashboard';
import Contragents from './components/contragents/contragents';
import Dialogs from './components/dialogs/dialogs';
import Orders from './components/orders/orders';
import Products from './components/products/products';
import Users from './components/users/users';
import Settings from './components/settings/settings';
import Companies from './components/companies/companies';
import Backup from './components/settings/backup';
import Pulse from './components/pulse/pulse';
import { serverFetch } from './server';
import { userLoaded } from './redux/actions';
import { showError, getStored, setStored } from './extensions/utils';
import { useDicts } from './extensions/dicts';
import { useSelectCompany } from './extensions/company';
import { LayoutContext, useLayout } from './extensions/layout';
import { useUnreadDialogs } from './extensions/dialogs';
import IncomingCalls from './components/dialogs/incoming-call';
import { useColorTheme } from './theme';
import './theme-dark.css';

const { Sider, Content } = Layout;

const ROLE_MANAGER = 10;

const NavMenu = ({ user }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const unreadDialogs = useUnreadDialogs();
    const { collapsed } = useLayout();

    const items = [
        user.isSuperadmin ? { key: '/pulse', icon: <DashboardOutlined />, label: 'Пульс' } : null,
        { key: '/tasks', icon: <UnorderedListOutlined />, label: 'Задачи' },
        { key: '/contragents', icon: <ContactsOutlined />, label: 'Контрагенты' },
        {
            key: '/dialogs',
            icon: collapsed
                ? <Badge dot={unreadDialogs > 0} offset={[2, 2]}><MessageOutlined /></Badge>
                : <MessageOutlined />,
            label: <span>Диалоги <Badge count={unreadDialogs} size="small" offset={[4, -2]} /></span>,
        },
        { key: '/orders', icon: <ShoppingCartOutlined />, label: 'Заказы' },
        { key: '/products', icon: <AppstoreOutlined />, label: 'Товары' },
        user.role === 0 ? { key: '/users', icon: <TeamOutlined />, label: 'Сотрудники' } : null,
        user.role <= ROLE_MANAGER
            ? { key: '/settings', icon: <SettingOutlined />, label: 'Настройки' }
            : null,
        user.isSuperadmin ? { key: '/companies', icon: <BankOutlined />, label: 'Фирмы' } : null,
        user.isSuperadmin ? { key: '/service', icon: <ToolOutlined />, label: 'Сервис' } : null,
    ].filter(Boolean);

    return (
        <Menu
            theme="dark"
            mode="inline"
            selectedKeys={[
                location.pathname.startsWith('/settings') ? '/settings'
                    : location.pathname.startsWith('/service') ? '/service'
                        : location.pathname,
            ]}
            items={items}
            onClick={({ key }) => navigate(key)}
        />
    );
};

// Переключатель фирмы в сайдбаре (только суперадмин)
const CompanySwitcher = ({ user }) => {
    const selectCompany = useSelectCompany();
    const [companies, setCompanies] = useState([]);

    useEffect(() => {
        serverFetch('company')
            .then((items) => setCompanies(items.filter((c) => c.active)))
            .catch(showError);
    }, []);

    const onChange = (id) => {
        const company = companies.find((c) => c.id === id);
        if (!company) return;
        selectCompany({ id, name: company.name });
    };

    return (
        <Select
            className="app-sider-company-select"
            size="small"
            showSearch
            optionFilterProp="label"
            placeholder="Выберите фирму"
            value={user.companyId ?? undefined}
            options={companies.map((c) => ({ value: c.id, label: c.name }))}
            onChange={onChange}
        />
    );
};

const AppLayout = ({ children, user }) => {
    const d = useDispatch();
    const navigate = useNavigate();
    const { colorTheme, setColorTheme } = useColorTheme();
    const [collapsed, setCollapsed] = useState(getStored('siderCollapsed', false));
    const dark = colorTheme === 'dark';
    useDicts();

    const onCollapse = (value) => {
        setCollapsed(value);
        setStored('siderCollapsed', value);
    };

    const onLogout = () => {
        serverFetch('auth/Logout', { method: 'POST' })
            .catch(showError)
            .finally(() => d(userLoaded(null)));
    };

    return (
        <LayoutContext.Provider value={{ collapsed }}>
            <Layout className="app-layout">
                <Sider collapsible collapsed={collapsed} collapsedWidth={48} onCollapse={onCollapse}>
                    <button
                        type="button"
                        className="app-logo"
                        title="Главная"
                        onClick={() => navigate(user.isSuperadmin && user.companyId == null ? '/pulse' : '/')}
                    >
                        <img className="app-logo-icon" src={`${process.env.PUBLIC_URL}/botva-tasks-icon.svg`} alt="" />
                        {!collapsed && <span>BOTVA TASKS</span>}
                    </button>
                    {!collapsed && (user.isSuperadmin
                        ? <CompanySwitcher user={user} />
                        : user.companyName && <div className="app-sider-company">{user.companyName}</div>)}
                    <NavMenu user={user} />
                    <div className="app-sider-footer">
                        {!collapsed && <div className="app-sider-user" title={user.userName}>{user.userName}</div>}
                        <Tooltip title={collapsed ? (dark ? 'Светлая тема' : 'Тёмная тема') : ''} placement="right">
                            <Button
                                className="app-sider-theme"
                                size="small"
                                block={!collapsed}
                                icon={dark ? <BulbOutlined /> : <MoonOutlined />}
                                onClick={() => setColorTheme(dark ? 'light' : 'dark')}
                            >
                                {!collapsed && (dark ? 'Светлая тема' : 'Тёмная тема')}
                            </Button>
                        </Tooltip>
                        <Tooltip title={collapsed ? `${user.userName} — выход` : ''} placement="right">
                            <Button
                                size="small"
                                block={!collapsed}
                                icon={<PoweroffOutlined />}
                                onClick={onLogout}
                            >
                                {!collapsed && 'Выход'}
                            </Button>
                        </Tooltip>
                    </div>
                </Sider>
                <Content className="app-content" key={user.companyId ?? 'none'}>{children}</Content>
                {user.companyId != null && <IncomingCalls companyId={user.companyId} />}
            </Layout>
        </LayoutContext.Provider>
    );
};

const App = () => {
    const user = useSelector((s) => s.user);

    if (!user) {
        return <Auth />;
    }

    // Суперадмин без выбранной компании — только экран фирм
    const superAdminNoCompany = user.isSuperadmin && user.companyId == null;

    const page = (obj) => <AppLayout user={user}>{obj}</AppLayout>;

    const router = createBrowserRouter([
        { path: '/', element: page(superAdminNoCompany ? <Navigate to="/pulse" replace /> : <Dashboard />) },
        { path: '/pulse', element: page(user.isSuperadmin ? <Pulse /> : <Navigate to="/" replace />) },
        { path: '/tasks', element: page(superAdminNoCompany ? <Navigate to="/companies" replace /> : <Tasks />) },
        { path: '/my-tasks', element: <Navigate to={user.isSuperadmin ? '/pulse' : '/tasks'} replace /> },
        { path: '/contragents', element: page(superAdminNoCompany ? <Navigate to="/companies" replace /> : <Contragents />) },
        { path: '/dialogs', element: page(superAdminNoCompany ? <Navigate to="/companies" replace /> : <Dialogs />) },
        { path: '/orders', element: page(superAdminNoCompany ? <Navigate to="/companies" replace /> : <Orders />) },
        { path: '/products', element: page(superAdminNoCompany ? <Navigate to="/companies" replace /> : <Products />) },
        { path: '/users', element: page(superAdminNoCompany ? <Navigate to="/companies" replace /> : user.role === 0 ? <Users /> : <Navigate to="/tasks" replace />) },
        { path: '/settings/:page?', element: page(superAdminNoCompany ? <Navigate to="/companies" replace /> : <Settings />) },
        { path: '/companies', element: page(user.isSuperadmin ? <Companies /> : <Navigate to="/tasks" replace />) },
        { path: '/service', element: page(user.isSuperadmin ? <Backup /> : <Navigate to="/tasks" replace />) },
        { path: '*', element: <Navigate to="/" replace /> },
    ]);

    return <RouterProvider router={router} />;
};

export default memo(App);
